import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, CheckCircle2, ClipboardList, Target, ArrowRight, Bookmark, History,
  Trophy, Award, CalendarDays, Video, Flame, Star, Zap, GraduationCap, Gamepad2, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import CourseBadges from "../components/CourseBadges";
import { pick } from "../lib/pick";

const BADGE_ICONS = { first_step: Flame, perfect: Star, consistent: Zap, graduate: GraduationCap, elite: Trophy, game_master: Gamepad2 };
const ALL_BADGES = ["first_step", "perfect", "consistent", "graduate", "elite", "game_master"];

const fmtDateTime = (value) =>
  value ? new Date(value).toLocaleString("el-GR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [myCourses, setMyCourses] = useState(null);
  const [bookmarks, setBookmarks] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/my-courses").then((r) => setMyCourses(r.data)).catch(() => setMyCourses([]));
    api.get("/bookmarks").then((r) => setBookmarks(r.data)).catch(() => setBookmarks([]));
    api.get("/quiz-history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, []);

  const statCards = stats
    ? [
        { icon: BookOpen, label: t("dash.enrolled"), value: stats.enrolled_courses },
        { icon: CheckCircle2, label: t("dash.completed"), value: stats.completed_lessons },
        { icon: ClipboardList, label: t("dash.attempts"), value: stats.quiz_attempts },
        { icon: Target, label: t("dash.avg"), value: `${stats.avg_score}%` },
      ]
    : [];

  const level = stats?.level;
  const earned = stats?.badges || [];
  const certificates = stats?.certificates || [];
  const nextSession = stats?.next_session;

  return (
    <div data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-20">
        <span className="badge-lime">{user?.name}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="dashboard-title">{t("dash.title")}</h1>
        <p className="text-[#64748B] mt-3">{t("dash.subtitle")}</p>

        {/* XP + next session */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-12">
          <div className="lg:col-span-7 panel-lime p-7 sm:p-9" data-testid="dashboard-xp-panel">
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#475569]">{t("gam.xp")}</p>
                <p className="font-heading font-black text-5xl sm:text-6xl mt-2" data-testid="dashboard-xp-value">{stats?.xp ?? 0}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#475569]">{t("gam.level")}</p>
                <p className="font-heading font-black text-3xl mt-2" data-testid="dashboard-level">{level?.level || "Rookie"}</p>
              </div>
            </div>
            <div className="h-3 bg-[#F8FAFC] rounded-full mt-6 overflow-hidden">
              <div className="h-full bg-[#1D4ED8] rounded-full transition-all duration-700" style={{ width: `${level?.progress ?? 0}%` }} />
            </div>
            {level?.next_level && (
              <p className="text-xs font-semibold text-[#475569] mt-3">
                {Math.max((level.next_at || 0) - (stats?.xp || 0), 0)} XP {t("gam.next")} · {level.next_level}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-7">
              <Link data-testid="dashboard-leaderboard-link" to="/leaderboard" className="btn-dark !px-6 !py-2.5 text-sm">
                <Trophy className="w-4 h-4" /> {t("gam.leaderboardCta")}
              </Link>
              <Link data-testid="dashboard-learn-link" to="/courses" className="btn-outline-light !px-6 !py-2.5 text-sm">
                <Sparkles className="w-4 h-4" /> {t("nav.academy")}
              </Link>
              <Link data-testid="dashboard-certificates-link" to="/certificates" className="btn-outline-light !px-6 !py-2.5 text-sm">
                <Award className="w-4 h-4" /> {t("nav.certificates")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 card-dark p-7" data-testid="dashboard-next-session">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#1D4ED8]" />
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#64748B]">{t("sessions.next")}</p>
            </div>
            {nextSession ? (
              <>
                <p className="font-heading font-black text-2xl text-[#0B1B3A] mt-5">{nextSession.title}</p>
                <p className="text-sm text-[#1D4ED8] mt-2">{pick(nextSession, "course_title", lang)}</p>
                <p className="text-sm text-[#475569] mt-1">{fmtDateTime(nextSession.starts_at)}</p>
                {nextSession.meeting_url && (
                  <a data-testid="dashboard-session-join" href={nextSession.meeting_url} target="_blank" rel="noreferrer" className="btn-lime mt-6 !px-6 !py-2.5 text-sm">
                    <Video className="w-4 h-4" /> {t("sessions.join")}
                  </a>
                )}
              </>
            ) : (
              <p className="text-[#64748B] mt-5" data-testid="dashboard-no-session">{t("sessions.empty")}</p>
            )}
          </div>
        </div>

        <CourseBadges />

        {/* Badges */}
        <div className="flex items-center gap-3 mt-14">
          <Award className="w-6 h-6 text-[#1D4ED8]" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("gam.badges")}</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6" data-testid="dashboard-badges">
          {ALL_BADGES.map((key) => {
            const Icon = BADGE_ICONS[key];
            const has = earned.includes(key);
            return (
              <div
                key={key}
                className={`rounded-[24px] p-6 border text-center ${has ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-[#94A3B8] border-[#E2E8F0]"}`}
                data-testid={`badge-${key}${has ? "-earned" : "-locked"}`}
              >
                <Icon className="w-7 h-7 mx-auto" />
                <p className="text-xs font-bold uppercase tracking-[0.14em] mt-3">{t(`badge.${key}`)}</p>
              </div>
            );
          })}
        </div>

        {/* Certificates */}
        <div className="flex items-center gap-3 mt-14">
          <GraduationCap className="w-6 h-6 text-[#1D4ED8]" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("cert.title")}</h2>
        </div>
        {certificates.length === 0 ? (
          <p className="text-[#64748B] mt-4" data-testid="dashboard-certificates-empty">{t("cert.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="dashboard-certificates">
            {certificates.map((c) => (
              <div key={c.course_id} className="card-cream p-6 flex flex-col" data-testid={`certificate-card-${c.course_id}`}>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{c.code}</span>
                <p className="font-heading font-black text-xl mt-2 flex-1">{pick(c, "course_title", lang)}</p>
                <Link data-testid={`certificate-open-${c.course_id}`} to={`/certificate/${c.course_id}`} className="btn-dark mt-5 !px-5 !py-2 text-xs self-start">
                  {t("cert.view")} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {statCards.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="card-dark p-6 hover:border-[#1D4ED8] transition-colors duration-300"
                data-testid={`dashboard-stat-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#1D4ED8] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#0B1B3A]" />
                </div>
                <p className="font-heading font-black text-4xl mt-4 text-[#0B1B3A]" data-testid={`dashboard-stat-value-${i}`}>{s.value}</p>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B] mt-1">{s.label}</p>
              </motion.div>
            );
          })}
        </div>

        <h2 className="font-heading font-black text-2xl sm:text-3xl mt-16 text-[#0B1B3A]">{t("dash.myCourses")}</h2>

        {myCourses === null ? (
          <p className="mt-6 text-[#64748B]">{t("common.loading")}</p>
        ) : myCourses.length === 0 ? (
          <div className="card-dark p-12 mt-6 text-center" data-testid="dashboard-empty">
            <p className="text-[#64748B]">{t("dash.empty")}</p>
            <Link data-testid="dashboard-browse-button" to="/courses" className="btn-blue mt-6">
              {t("dash.browse")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            {myCourses.map((item, i) => (
              <motion.div
                key={item.course.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="card-dark overflow-hidden hover:border-[#1D4ED8] transition-colors duration-300"
                data-testid={`my-course-card-${item.course.id}`}
              >
                <div className="flex">
                  <div className="w-32 shrink-0 hidden sm:block bg-[#EEF4FF]">
                    {item.course.thumbnail && <img src={item.course.thumbnail} alt={item.course.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{pick(item.course, "title", lang)}</h3>
                      <span className="font-heading font-black text-2xl text-[#1D4ED8]" data-testid={`my-course-progress-${item.course.id}`}>{item.progress}%</span>
                    </div>
                    <div className="h-2 bg-[#F1F5F9] rounded-full mt-4 overflow-hidden">
                      <div className="h-full bg-[#1D4ED8] rounded-full transition-all duration-500" style={{ width: `${item.progress}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs font-semibold text-[#64748B]">
                        {item.completed_lessons} / {item.total_lessons} {t("courses.lessons")}
                      </span>
                      <Link data-testid={`my-course-continue-${item.course.id}`} to={`/courses/${item.course.id}`} className="btn-lime !px-5 !py-2 text-xs">
                        {t("dash.continue")} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* BOOKMARKS */}
        <div className="flex items-center gap-3 mt-16">
          <Bookmark className="w-6 h-6 text-[#1D4ED8]" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("dash.bookmarks")}</h2>
        </div>
        {bookmarks === null ? (
          <p className="text-[#64748B] mt-4">{t("common.loading")}</p>
        ) : bookmarks.length === 0 ? (
          <p className="text-[#64748B] mt-4" data-testid="dashboard-bookmarks-empty">{t("dash.bookmarksEmpty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="dashboard-bookmarks">
            {bookmarks.map((b) => (
              <div key={b.lesson_id} className="card-dark p-6 flex flex-col hover:border-[#1D4ED8] transition-colors duration-300" data-testid={`bookmark-card-${b.lesson_id}`}>
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{pick(b, "course_title", lang)}</span>
                <p className="font-bold mt-2 flex-1 text-[#0B1B3A]">{pick(b, "lesson_title", lang)}</p>
                <Link data-testid={`bookmark-open-${b.lesson_id}`} to={`/lessons/${b.lesson_id}`} className="btn-blue mt-5 !px-5 !py-2 text-xs self-start">
                  {t("dash.open")} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* QUIZ HISTORY */}
        <div className="flex items-center gap-3 mt-16">
          <History className="w-6 h-6 text-[#1D4ED8]" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("dash.history")}</h2>
        </div>
        {history === null ? (
          <p className="text-[#64748B] mt-4">{t("common.loading")}</p>
        ) : history.length === 0 ? (
          <p className="text-[#64748B] mt-4" data-testid="dashboard-history-empty">{t("dash.historyEmpty")}</p>
        ) : (
          <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="dashboard-history">
            {history.slice(0, 15).map((h, i) => (
              <div key={i} className="flex flex-wrap items-center gap-4 p-4 hover:bg-[#F8FAFC] transition-colors duration-200" data-testid={`history-row-${i}`}>
                <span className={`font-heading font-black text-2xl w-20 ${h.passed ? "text-[#1D4ED8]" : "text-[#DC2626]"}`}>{h.percentage}%</span>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-bold text-sm text-[#0B1B3A]">{pick(h, "lesson_title", lang)}</p>
                  <p className="text-xs text-[#64748B]">{pick(h, "course_title", lang)}</p>
                </div>
                <span className="text-xs text-[#94A3B8]">{new Date(h.created_at).toLocaleString()}</span>
                <Link data-testid={`history-open-${i}`} to={`/lessons/${h.lesson_id}`} className="text-xs font-bold uppercase tracking-wider text-[#1D4ED8] hover:underline">
                  {t("dash.open")}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
